
import Button from '../../../components/base/Button';

export default function ResourcesSection() {
  const resources = [
    {
      category: "Policy Guides",
      items: [
        {
          title: "Healthcare Policy Development Framework",
          description: "Comprehensive guide to understanding and participating in healthcare policy development processes.",
          type: "PDF Guide",
          pages: 45,
          icon: "ri-book-line"
        },
        {
          title: "Government Relations Best Practices",
          description: "Strategies for effective engagement with elected officials and government agencies.",
          type: "Interactive Guide",
          pages: 32,
          icon: "ri-government-line"
        },
        {
          title: "Legislative Process in Canada",
          description: "Step-by-step explanation of how bills become laws in the Canadian parliamentary system.",
          type: "Video Series",
          pages: "6 videos",
          icon: "ri-play-circle-line"
        }
      ]
    },
    {
      category: "Advocacy Tools",
      items: [
        {
          title: "Campaign Planning Template",
          description: "Professional template for organizing and executing successful advocacy campaigns.",
          type: "Template",
          pages: "12 sections",
          icon: "ri-file-list-3-line"
        },
        {
          title: "Media Relations Kit",
          description: "Press release templates, media contact lists, and interview preparation guides.",
          type: "Resource Kit",
          pages: 28,
          icon: "ri-newspaper-line"
        },
        {
          title: "Social Media Advocacy Guide",
          description: "Tools and strategies for effective digital advocacy and online engagement.",
          type: "Digital Guide",
          pages: 38,
          icon: "ri-share-line"
        }
      ]
    },
    {
      category: "Training Materials",
      items: [
        {
          title: "Public Speaking for Advocates",
          description: "Training materials for developing effective public speaking and presentation skills.",
          type: "Workshop Materials",
          pages: "4 hours",
          icon: "ri-mic-line"
        },
        {
          title: "Policy Analysis Fundamentals",
          description: "Learn to analyze proposed legislation and develop evidence-based position statements.",
          type: "Online Course",
          pages: "8 modules",
          icon: "ri-search-eye-line"
        },
        {
          title: "Coalition Building Strategies",
          description: "Building effective partnerships and coalitions for stronger advocacy impact.",
          type: "Case Studies",
          pages: 42,
          icon: "ri-team-line"
        }
      ]
    }
  ];

  const webinars = [
    {
      title: "Navigating Federal Healthcare Policy Changes",
      date: "February 15, 2025",
      time: "2:00 PM EST",
      presenter: "Dr. Sarah Mitchell, Policy Director",
      registered: 850,
      status: "Upcoming"
    },
    {
      title: "Provincial Advocacy: Success Stories and Strategies",
      date: "March 8, 2025",
      time: "1:00 PM EST",
      presenter: "Michael Chen, Government Relations",
      registered: 420,
      status: "Registration Open"
    },
    {
      title: "Building Grassroots Support for Healthcare Initiatives",
      date: "March 22, 2025",
      time: "3:00 PM EST",
      presenter: "Dr. Amanda Roberts, Campaign Coordinator",
      registered: 180,
      status: "Registration Open"
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-archive-line text-2xl text-indigo-600"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Advocacy Resources
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Access comprehensive tools, guides, and training materials to enhance your advocacy effectiveness
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {resources.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                {category.category}
              </h3>
              <div className="space-y-4">
                {category.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="bg-white rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className={`${item.icon} text-lg text-blue-600`}></i>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {item.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          {item.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <span className="bg-gray-100 px-2 py-1 rounded mr-2">
                              {item.type}
                            </span>
                            <span>{item.pages}</span>
                          </div>
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer whitespace-nowrap">
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-50 rounded-lg p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Upcoming Training Webinars
            </h3>
            <p className="text-gray-600">
              Join our expert-led training sessions to enhance your advocacy skills and stay updated on policy developments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {webinars.map((webinar, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    webinar.status === 'Upcoming' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {webinar.status}
                  </span>
                  <span className="text-sm text-gray-500">{webinar.registered} registered</span>
                </div>

                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {webinar.title}
                </h4>

                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-calendar-line mr-2"></i>
                    {webinar.date}
                  </div>
                  <div className="flex items-center">
                    <i className="ri-time-line mr-2"></i>
                    {webinar.time}
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line mr-2"></i>
                    {webinar.presenter}
                  </div>
                </div>

                <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                  Register Now
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need Additional Resources?
          </h3>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Contact our advocacy team for personalized support and additional resources.
          </p>
          <Button className="bg-indigo-600 text-white hover:bg-indigo-700 px-8 py-3 text-lg font-medium whitespace-nowrap">
            Contact Advocacy Team
          </Button>
        </div>
      </div>
    </section>
  );
}
